import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Users,
  Building2,
  Mail,
  CreditCard,
  Calendar,
  Globe,
  Settings,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  ExternalLink,
  Shield,
  HardDrive,
  FileEdit,
  Briefcase,
  Send
} from 'lucide-react'
import { INDUSTRIES } from '../../utils/industrySchemas'
import LoadingSpinner from '../common/LoadingSpinner'
import AdminNotes from './AdminNotes'
import ClientPermissions from './ClientPermissions'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card'
import { Button } from '../ui/button'

const ClientDetail = () => {
  const navigate = useNavigate()
  const { clientId } = useParams()
  const queryClient = useQueryClient()

  // Fetch client data
  const { data: client, isLoading, error } = useQuery(
    ['client', clientId],
    () => apiService.get(`/clients/profile?clientId=${clientId}`).then(res => res.data),
    { 
      enabled: !!clientId,
      retry: 1
    }
  )

  // Fetch plans to resolve custom plan names
  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  )

  // Fetch client billing details
  const { data: billingData } = useQuery(
    ['client-billing', clientId],
    () => apiService.get(`/billing/client/${clientId}`).then(res => res.data),
    { 
      enabled: !!clientId,
      retry: 1
    }
  )

  // Fetch client domains
  const { data: clientDomains } = useQuery(
    ['client-domains', clientId],
    () => apiService.get('/domains').then(res => {
      const domains = res.data?.domains || [];
      const filtered = domains.filter(d => d.client_id === clientId);
      return filtered;
    }),
    { enabled: !!clientId }
  )

  // Resend invitation email mutation
  const resendInvitationMutation = useMutation(
    () => apiService.post(`/clients/${clientId}/resend-invitation`),
    {
      onSuccess: () => toast.success(`Invitation email resent to ${client?.email}`),
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to resend invitation')
    }
  )

  // Delete client mutation
  const deleteClientMutation = useMutation(
    () => apiService.delete(`/clients/${clientId}`),
    {
      onSuccess: () => {
        toast.success('Client deleted successfully')
        navigate('/admin/clients')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete client')
      }
    }
  )

  const handleDeleteClient = () => {
    if (confirm(`Are you sure you want to delete ${client?.company_name}? This action cannot be undone and will delete all associated sites and data.`)) {
      deleteClientMutation.mutate()
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading client details..." />
      </div>
    )
  }

  if (error || !client) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-error-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
          Client Not Found
        </h3>
        <p className="text-neutral-500 dark:text-neutral-400 mb-6">
          The client you're looking for doesn't exist or has been deleted.
        </p>
        <Button
          onClick={() => navigate('/admin/clients')}
        >
          Back to Clients
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/clients')}
            className="text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
              {client.company_name}
            </h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Client Details & Management
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={() => resendInvitationMutation.mutate()}
            disabled={resendInvitationMutation.isLoading}
            className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
          >
            {resendInvitationMutation.isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Resend Invitation
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/clients/${clientId}/edit`)}
            className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Client
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteClient}
            disabled={deleteClientMutation.isLoading}
          >
            {deleteClientMutation.isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </>
            )}
          </Button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <div className="p-2 bg-accent-100 dark:bg-accent-900/20 rounded-lg">
                    <Users className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                  </div>
                  <span>Client Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Contact Name
                    </label>
                    <p className="text-sm text-neutral-900 dark:text-neutral-100">
                      {client.user_name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Email Address
                    </label>
                    <p className="text-sm text-neutral-900 dark:text-neutral-100">
                      {client.email}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Company Name
                    </label>
                    <p className="text-sm text-neutral-900 dark:text-neutral-100">
                      {client.company_name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Industry
                    </label>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200">
                      <span>{INDUSTRIES[client.industry]?.icon || '🏢'}</span>
                      <span>{INDUSTRIES[client.industry]?.label || 'General Business'}</span>
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Custom Domains */}
          <CustomDomainsSection clientDomains={clientDomains} clientId={clientId} navigate={navigate} />

          {/* Admin Notes */}
          <AdminNotes clientId={clientId} />

          {/* Client Permissions */}
          <ClientPermissions clientId={clientId} />

          {/* Subscription Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                    <CreditCard className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <span>Subscription & Billing</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Current Plan
                    </label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      client.subscription_plan === 'basic' ? 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200' :
                      client.subscription_plan === 'pro' ? 'bg-accent-100 text-accent-700 dark:bg-accent-900 dark:text-accent-200' :
                      'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200'
                    }`}>
                      {(plansRaw?.[client.subscription_plan]?.name || client.subscription_plan)?.toUpperCase() || 'BASIC'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Monthly Cost
                    </label>
                    <p className="text-sm text-neutral-900 dark:text-neutral-100">
                      {billingData?.plan ? formatCurrency(billingData.plan.price) : 'Loading...'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Edit Quota
                    </label>
                    <p className="text-sm text-neutral-900 dark:text-neutral-100">
                      {client.edits_used || 0} / {client.edit_quota || client.monthly_edit_limit || 50} edits
                    </p>
                    <div className="w-full bg-neutral-200 dark:bg-[#2f2b41] rounded-full h-2 mt-1">
                      <div
                        className="h-2 rounded-full bg-accent-500"
                        style={{ 
                          width: `${Math.min(((client.edits_used || 0) / (client.edit_quota || client.monthly_edit_limit || 50)) * 100, 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Next Billing Date
                    </label>
                    <p className="text-sm text-neutral-900 dark:text-neutral-100">
                      {billingData?.nextBillingDate ? formatDate(billingData.nextBillingDate) : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Usage Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <div className="p-2 bg-success-100 dark:bg-success-900/20 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-success-600 dark:text-success-400" />
                  </div>
                  <span>Usage Statistics</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      <Globe className="h-4 w-4 text-neutral-400" />
                    </div>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                      {client.site_count || 0}
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Total Sites
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                      {client.published_sites || 0}
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Published Sites
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      <FileEdit className="h-4 w-4 text-accent-500" />
                    </div>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                      {client.edits_used || 0}
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Edits This Month
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      <HardDrive className="h-4 w-4 text-neutral-400" />
                    </div>
                    <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                      {client.storage_used_mb ? `${client.storage_used_mb} MB` : '0 MB'}
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Storage Used
                    </p>
                    {client.file_count > 0 && (
                      <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
                        {client.file_count} file{client.file_count !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={() => navigate('/admin/sites/new', { state: { clientId: client.id } })}
                  className="w-full"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  Create New Site
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/clients', { state: { filter: client.subscription_plan } })}
                  className="w-full"
                >
                  <Users className="h-4 w-4 mr-2" />
                  View Similar Clients
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/admin/billing`, { state: { clientId: client.id } })}
                  className="w-full"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  View Billing Details
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Account Status */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Account Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Account:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    client.is_active
                      ? 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-200'
                      : 'bg-error-100 text-error-700 dark:bg-error-900/20 dark:text-error-200'
                  }`}>
                    {client.is_active ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Billing:</span>
                  {(() => {
                    const isFreePlan = plansRaw?.[client.subscription_plan]?.price === 0
                    const s = isFreePlan ? 'active' : client.billing_status
                    const map = {
                      active:   'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-200',
                      trialing: 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-200',
                      past_due: 'bg-error-100 text-error-700 dark:bg-error-900/20 dark:text-error-200',
                      unpaid:   'bg-error-100 text-error-700 dark:bg-error-900/20 dark:text-error-200',
                    }
                    const labels = { active: 'Active', trialing: 'Trialing', past_due: 'Past due', unpaid: 'Unpaid' }
                    const cls = map[s] || 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-200'
                    const label = labels[s] || 'Pending payment'
                    return (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>
                        {label}
                      </span>
                    )
                  })()}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Member Since:</span>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">
                    {client.created_at ? formatDate(client.created_at) : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Last Login:</span>
                  <span className="text-sm text-neutral-900 dark:text-neutral-100">
                    {client.last_login ? formatDate(client.last_login) : 'Never'}
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

// Custom Domains Section Component
const CustomDomainsSection = ({ clientDomains, clientId, navigate }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />
      case 'dns_configured':
        return <AlertCircle className="h-4 w-4 text-blue-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
      case 'active':
        return 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-200'
      case 'pending':
        return 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-200'
      case 'dns_configured':
        return 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-200'
      case 'failed':
        return 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-200'
      default:
        return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200'
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                <Globe className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <span>Custom Domains</span>
            </CardTitle>
            <span className="text-sm text-neutral-500 dark:text-neutral-400">
              {clientDomains?.length || 0} domain{(clientDomains?.length !== 1) ? 's' : ''}
            </span>
          </div>
        </CardHeader>
        
        <CardContent>
        {clientDomains && clientDomains.length > 0 ? (
          <div className="space-y-3">
            {clientDomains.map((domain) => {
              const fullDomain = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
              return (
                <div key={domain.id} className="flex items-center justify-between p-3 border border-neutral-200 dark:border-[#3a3548] rounded-lg hover:bg-neutral-50 dark:hover:bg-[#2a2538] transition-colors duration-200">
                  <div className="flex items-center space-x-3">
                    <Globe className="h-5 w-5 text-neutral-400" />
                    <div>
                      <div className="font-medium text-neutral-900 dark:text-neutral-100">
                        {fullDomain}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(domain.status)}`}>
                          {getStatusIcon(domain.status)}
                          <span className="ml-1 capitalize">{domain.status.replace('_', ' ')}</span>
                        </span>
                        {!!domain.ssl_enabled && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-200">
                            <Shield className="h-3 w-3 mr-1" />
                            SSL
                          </span>
                        )}
                      </div>
                      {domain.verified_at && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                          Verified on {new Date(domain.verified_at).toLocaleDateString()}
                        </p>
                      )}
                      {domain.error_message && (
                        <p className="text-xs text-error-600 dark:text-error-400 mt-1">{domain.error_message}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {domain.status === 'verified' && domain.azure_published_url && (
                      <a
                        href={domain.azure_published_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        title="View Live Site"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <Globe className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100 mb-2">
              No custom domains
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
              This client hasn't set up any custom domains yet.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                console.log('Add Domain button clicked, navigating to:', `/admin/clients/${clientId}/edit`);
                console.log('clientId:', clientId);
                console.log('navigate function:', navigate);
                try {
                  navigate(`/admin/clients/${clientId}/edit`);
                } catch (error) {
                  console.error('Navigation error:', error);
                  toast.error('Navigation failed: ' + error.message);
                }
              }}
            >
              Add Domain
            </Button>
          </div>
        )}
        </CardContent>
      </Card>
    </motion.div>
  )
}

export default ClientDetail